import type { Level } from '../core/hp/types';
import type { CameraPose } from '../render/three/Viewer';
import { activeRoom, indicatorPoint, minimapRooms } from '../state/minimap';
import { containsPoint } from '../state/sceneGesture';
import type { FloorRef, ParticipantTarget, PresenceContext, PresenceEvent, PresenceInput, PresenceView, PresenceViewport, WorldPoint } from './protocol';
import type { Viewport } from '../render/plan/PlanView';
import { PLAN_SCALE_LIMITS } from '../state/projectView';

export type LocalPresenceView = { kind: 'floor' | 'ceiling' | 'orbit' | 'walk' }
  | { kind: 'wall'; wallId: string; side: 'front' | 'back' };
export type PresenceMarker = { kind: 'cursor' | 'pose'; point: WorldPoint; yaw: number | null; contextKey: string };

const record = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
const point = (value: unknown): value is WorldPoint => record(value) && ['x', 'y', 'z'].every(key => typeof value[key] === 'number' && Number.isFinite(value[key]));
const finite = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value) && Math.abs(value) <= 1e100;
const tuple = (value: unknown, length: number): value is number[] => Array.isArray(value) && value.length === length && value.every(finite);
const fov = (value: unknown): value is number => finite(value) && value >= 5 && value <= 170;

export function isPresenceViewport(value: unknown, mode: PresenceInput['mode'], level: Level): boolean {
  if (value === undefined || value === null) return true;
  if (!record(value)) return false;
  const following = value.following;
  if (!(following === null || record(following) && typeof following.userId === 'string' && following.userId.length > 0 && following.userId.length <= 512
    && typeof following.clientId === 'string' && following.clientId.length > 0 && following.clientId.length <= 512)) return false;
  if (value.kind === 'floor' || value.kind === 'ceiling') return mode === 'plan'
    && record(value.centre) && finite(value.centre.x) && finite(value.centre.z)
    && record(value.span) && finite(value.span.width) && value.span.width > 0 && finite(value.span.height) && value.span.height > 0;
  if (value.kind === 'wall') return mode === 'wall' && level.walls.some(wall => wall.id === value.wallId)
    && (value.side === 'front' || value.side === 'back') && finite(value.zoom) && value.zoom >= .25 && value.zoom <= 8
    && record(value.pan) && finite(value.pan.x) && finite(value.pan.y);
  if (mode !== 'scene' || value.kind !== 'orbit' && value.kind !== 'walk' || !record(value.camera)) return false;
  const camera = value.camera;
  return tuple(camera.position, 3) && tuple(camera.target, 3) && tuple(camera.quaternion, 4) && Math.hypot(...camera.quaternion) > 0
    && fov(camera.fov) && (camera.walkFov === null || fov(camera.walkFov)) && finite(camera.yaw)
    && finite(camera.pitch) && Math.abs(camera.pitch) <= Math.PI && ['full', 'cutaway', 'low'].includes(String(value.wallMode))
    && typeof value.showCeiling === 'boolean' && (value.isolatedRoom === null || level.rooms.some(room => room.index === value.isolatedRoom));
}

export function capturePlanViewport(viewport: Viewport, width: number, height: number, kind: 'floor' | 'ceiling'): PresenceViewport | null {
  if (!(width > 0 && height > 0)) return null;
  return { kind, centre: { ...viewport.centre }, span: { width: viewport.scale * width, height: viewport.scale * height }, following: null };
}

export function applyPlanViewport(viewport: Extract<PresenceViewport, { kind: 'floor' | 'ceiling' }>, width: number, height: number): Viewport | null {
  if (!(width > 0 && height > 0)) return null;
  return { centre: { ...viewport.centre }, scale: Math.min(PLAN_SCALE_LIMITS.max, Math.max(PLAN_SCALE_LIMITS.min,
    viewport.span.width / width, viewport.span.height / height)) };
}

export function isPresenceContext(value: unknown, mode: PresenceInput['mode'], cursor: PresenceInput['cursor']): boolean {
  if (!['plan', 'wall', 'scene'].includes(mode) || cursor !== null && !point(cursor)) return false;
  if (value === undefined || value === null) return true;
  if (!record(value) || !record(value.view)) return false;
  const view = value.view;
  if (view.kind === 'orbit' || view.kind === 'walk') {
    // A 3D view may carry a pointer too: the world point under it, drawn in the others' 3D views.
    return mode === 'scene' && (value.pose === null || record(value.pose)
      && point(value.pose.position) && typeof value.pose.yaw === 'number' && Number.isFinite(value.pose.yaw));
  }
  if (value.pose !== null) return false;
  if (view.kind === 'floor') return mode === 'plan';
  if (view.kind === 'ceiling') return mode === 'plan' && Number.isSafeInteger(view.roomIndex);
  return view.kind === 'wall' && mode === 'wall' && typeof view.wallId === 'string' && view.wallId.length > 0
    && (view.side === 'front' || view.side === 'back');
}

export function capturePresence(view: LocalPresenceView, level: Level, cursor: WorldPoint | null,
  camera: CameraPose | null): Pick<PresenceInput, 'mode' | 'cursor' | 'presence'> {
  if (view.kind === 'orbit' || view.kind === 'walk') {
    let pose = null;
    if (camera?.mode === view.kind && point({ ...camera.position, y: camera.height }) && Number.isFinite(camera.yaw)) {
      const room = view.kind === 'orbit' ? activeRoom(minimapRooms(level), camera.target) : null;
      const position = room ? indicatorPoint(camera.position, room, containsPoint(room.polygon, camera.target) ? camera.target : undefined) : camera.position;
      pose = { position: { ...position, y: camera.height }, yaw: camera.yaw };
    }
    return { mode: 'scene', cursor: cursor && point(cursor) ? cursor : null, presence: { view: { kind: view.kind }, pose } };
  }
  if (view.kind === 'ceiling') {
    const room = cursor && point(cursor) ? level.rooms.find(value => containsPoint(value.polygon, cursor)) : null;
    return room ? { mode: 'plan', cursor, presence: { view: { kind: 'ceiling', roomIndex: room.index }, pose: null } }
      : { mode: 'plan', cursor: null, presence: null };
  }
  const mode = view.kind === 'wall' ? 'wall' : 'plan';
  if (view.kind === 'wall' && !level.walls.some(wall => wall.id === view.wallId)) return { mode, cursor: null, presence: null };
  return { mode, cursor: cursor && point(cursor) ? cursor : null, presence: { view: view.kind === 'wall' ? view : { kind: 'floor' }, pose: null } };
}

const presentOn = (event: PresenceEvent, floor: FloorRef): event is PresenceEvent & { presence: PresenceContext } =>
  event.floor?.slot === floor.slot && event.floor.layoutVersion === floor.layoutVersion
  && isPresenceContext(event.presence, event.mode, event.cursor) && !!event.presence;
/** A wall plan shows only its own face and a ceiling plan only the ceiling; the floor plan and 3D show the whole floor. */
const seenFrom = (remote: PresenceView, view: LocalPresenceView) => view.kind === 'wall'
  ? remote.kind === 'wall' && remote.wallId === view.wallId && remote.side === view.side
  : view.kind !== 'ceiling' || remote.kind === 'ceiling';

export function presenceMarker(event: PresenceEvent, floor: FloorRef, view: LocalPresenceView, level: Level): PresenceMarker | null {
  if (!presentOn(event, floor)) return null;
  const remote = event.presence.view, camera = remote.kind === 'orbit' || remote.kind === 'walk';
  if (remote.kind === 'wall' && !level.walls.some(wall => wall.id === remote.wallId)) return null;
  if (remote.kind === 'ceiling' && !level.rooms.some(room => room.index === remote.roomIndex && event.cursor && containsPoint(room.polygon, event.cursor))) return null;
  if (!seenFrom(remote, view)) return null;
  const pose = event.presence.pose, context = (kind: PresenceMarker['kind']) => JSON.stringify([event.generation, floor.layoutVersion, floor.slot, remote, kind]);
  // A 3D visitor is drawn where they stand, in every view; anyone on a plan by their pointer, in 3D on the floor beneath it.
  if (camera) return pose ? { kind: 'pose', point: { ...pose.position, y: 0 }, yaw: pose.yaw, contextKey: context('pose') } : null;
  if (!event.cursor) return null;
  const scene = view.kind === 'orbit' || view.kind === 'walk';
  return { kind: 'cursor', point: scene ? { ...event.cursor, y: 0 } : event.cursor, yaw: null, contextKey: context('cursor') };
}

/** Who on this floor the wall or ceiling plan does not show («N more in other views»), in list order, the followed tab aside. */
export function hiddenParticipants(people: readonly PresenceEvent[], floor: FloorRef, view: LocalPresenceView,
  followed: ParticipantTarget | null): PresenceEvent[] {
  if (view.kind !== 'wall' && view.kind !== 'ceiling') return [];
  return people.filter(person => presentOn(person, floor) && !seenFrom(person.presence.view, view)
    && !(person.userId === followed?.userId && person.clientId === followed.clientId));
}

export function interpolatePresence(from: PresenceMarker, target: PresenceMarker, progress: number, reducedMotion: boolean): PresenceMarker {
  if (reducedMotion || from.contextKey !== target.contextKey || progress >= 1) return target;
  if (progress <= 0) return from;
  const mix = (a: number, b: number) => a + (b - a) * progress;
  const yaw = from.yaw !== null && target.yaw !== null
    ? from.yaw + Math.atan2(Math.sin(target.yaw - from.yaw), Math.cos(target.yaw - from.yaw)) * progress : target.yaw;
  return { ...target, point: { x: mix(from.point.x, target.point.x), y: mix(from.point.y, target.point.y), z: mix(from.point.z, target.point.z) }, yaw };
}
